import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { Alert, Button, Group, Modal, Select, SimpleGrid, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { TABLE_TEXTS } from '../format';
import { type DetailChanges, type DetailField, formChanges, initialValues } from './detailForm';

export type DetailMode = 'view' | 'edit';

/**
 * The popup of one library entry (AP-128): every field and the long text to
 * look at, and - for the administration, where the tab can write - the same
 * fields to change with Speichern / Abbrechen. Escape closes it; the table
 * that opened it puts the focus back on the row (hence no returnFocus here).
 */
export function DetailModal({ title, fields, mode, canEdit, onEdit, onCancelEdit, onSave, onClose, children }: {
  title: string;
  fields: readonly DetailField[];
  mode: DetailMode;
  canEdit: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (changes: DetailChanges) => Promise<void>;
  onClose: () => void;
  /** Below the fields in the view, e.g. the example objects of a benchmark. */
  children?: ReactNode;
}): JSX.Element {
  const narrow = useMediaQuery('(max-width: 48em)');
  const editable = canEdit && fields.some((f) => f.edit);
  return (
    <Modal opened onClose={onClose} title={title} size="xl" returnFocus={false} fullScreen={narrow}>
      {mode === 'edit' && editable
        ? <DetailForm fields={fields} onCancel={onCancelEdit} onSave={onSave} />
        : <DetailView fields={fields} onEdit={editable ? onEdit : undefined}>{children}</DetailView>}
    </Modal>
  );
}

function DetailView({ fields, onEdit, children }: { fields: readonly DetailField[]; onEdit?: () => void; children?: ReactNode }): JSX.Element {
  const shown = (f: DetailField): string => (f.value === null || f.value === '' ? '—' : f.value);
  return (
    <Stack gap="sm">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing={6}>
        {fields.filter((f) => !f.long).map((f) => (
          <Stack key={f.key} gap={0}>
            <Text size="xs" c="dimmed">{f.label}</Text>
            <Text size="sm" ff={f.mono ? 'monospace' : undefined}>{shown(f)}</Text>
          </Stack>
        ))}
      </SimpleGrid>
      {fields.filter((f) => f.long).map((f) => (
        <Stack key={f.key} gap={2}>
          <Text size="xs" c="dimmed">{f.label}</Text>
          <Text size={f.mono ? 'xs' : 'sm'} ff={f.mono ? 'monospace' : undefined} style={{ whiteSpace: 'pre-wrap' }}>{shown(f)}</Text>
        </Stack>
      ))}
      {children}
      {onEdit && <Group justify="flex-end"><Button variant="default" onClick={onEdit}>{TABLE_TEXTS.edit}</Button></Group>}
    </Stack>
  );
}

function DetailForm({ fields, onCancel, onSave }: { fields: readonly DetailField[]; onCancel: () => void; onSave: (changes: DetailChanges) => Promise<void> }): JSX.Element {
  const [values, setValues] = useState(() => initialValues(fields));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (key: string) => (v: string): void => setValues((all) => ({ ...all, [key]: v }));
  const submit = async (): Promise<void> => {
    const result = formChanges(fields, values);
    if ('error' in result) { setError(result.error); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave(result.changes);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : TABLE_TEXTS.saveFailed);
    } finally {
      setBusy(false);
    }
  };
  const editable = fields.filter((f) => f.edit);
  return (
    <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <Stack gap="sm">
        {error && <Alert color="red" py={6}>{error}</Alert>}
        {editable.map((f, i) => {
          const common = { label: f.label, value: values[f.key] ?? '', 'data-autofocus': i === 0 ? true : undefined, withAsterisk: f.required };
          if (f.edit === 'textarea') return <Textarea key={f.key} {...common} autosize minRows={f.long ? 6 : 2} maxRows={20} onChange={(e) => set(f.key)(e.currentTarget.value)} />;
          if (typeof f.edit === 'object') return <Select key={f.key} {...common} data={f.edit.options.map((o) => ({ ...o }))} allowDeselect={false} comboboxProps={{ withinPortal: true }} onChange={(v) => v && set(f.key)(v)} />;
          return <TextInput key={f.key} {...common} styles={f.mono ? { input: { fontFamily: 'monospace' } } : undefined} inputMode={f.edit === 'amount' ? 'decimal' : undefined} onChange={(e) => set(f.key)(e.currentTarget.value)} />;
        })}
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel} disabled={busy}>{TABLE_TEXTS.cancel}</Button>
          <Button type="submit" loading={busy}>{TABLE_TEXTS.save}</Button>
        </Group>
      </Stack>
    </form>
  );
}
